import { type ReactNode } from "react";
import { useDataQuality, type DataQualitySnapshot } from "../state/data-quality.tsx";
import { ServiceSwitcher } from "./ServiceSwitcher.tsx";

/**
 * The persistent data-quality strip (design 12.2; NFR-4 / FR-20).
 *
 * Always visible beneath the chrome so "a chart is never read without knowing what might be
 * missing". It reports three headline facts — sources silent, drops in range, sampling in effect —
 * plus clock skew and sequence gaps when known (NFR-4 AC2). The facts come from the data-quality
 * context, which the screens and the live stream populate; until something reports, each item shows
 * an honest "unknown" rather than an "all clear" ("unverifiable is never green", design 12.3).
 *
 * Every item is icon + text, never colour alone (design 12.4). Exact vs estimated counts are
 * distinguished (NFR-4 AC3): a sampled count is marked "est.".
 *
 * On the IDP / VCI / AIS service pages the right end of the row also hosts the service switcher
 * dropdown (it renders nothing elsewhere). It sits OUTSIDE the `role="status"` items container: a
 * live region must not contain an interactive control.
 */

type Severity = "ok" | "info" | "warn" | "unknown";

interface Item {
  readonly key: string;
  readonly icon: string;
  readonly text: string;
  readonly severity: Severity;
}

function silentItem(snapshot: DataQualitySnapshot): Item {
  const sources = snapshot.silentSources;
  if (sources === null) return { key: "silent", icon: "…", text: "Silent sources: unknown", severity: "unknown" };
  if (sources.length === 0) return { key: "silent", icon: "✓", text: "No silent sources", severity: "ok" };
  return {
    key: "silent",
    icon: "🔇",
    text: `${sources.length} source${sources.length === 1 ? "" : "s"} silent`,
    severity: "warn",
  };
}

function dropsItem(snapshot: DataQualitySnapshot): Item {
  const drops = snapshot.drops;
  if (drops === null) return { key: "drops", icon: "…", text: "Drops: unknown", severity: "unknown" };
  const total = drops.atSource + drops.atEdge + drops.inPipeline;
  if (total === 0) return { key: "drops", icon: "✓", text: "No drops in range", severity: "ok" };
  return {
    key: "drops",
    icon: "▼",
    text: `${total} dropped in range (source ${drops.atSource}, edge ${drops.atEdge}, pipeline ${drops.inPipeline})`,
    severity: "warn",
  };
}

function samplingItem(snapshot: DataQualitySnapshot): Item {
  const sampling = snapshot.sampling;
  if (sampling === null) return { key: "sampling", icon: "…", text: "Sampling: unknown", severity: "unknown" };
  if (!sampling.active) return { key: "sampling", icon: "✓", text: "No sampling (counts exact)", severity: "ok" };
  const rate = sampling.rate === null ? "" : ` at ${(sampling.rate * 100).toFixed(1)}%`;
  const est = sampling.exact ? "" : ", counts est.";
  return { key: "sampling", icon: "◍", text: `Sampling in effect${rate}${est}`, severity: "info" };
}

function skewItem(snapshot: DataQualitySnapshot): Item | null {
  if (snapshot.maxClockSkewMs === null || snapshot.maxClockSkewMs === 0) return null;
  return {
    key: "skew",
    icon: "⏱",
    text: `Clock skew up to ${Math.round(snapshot.maxClockSkewMs)} ms`,
    severity: "warn",
  };
}

function gapItem(snapshot: DataQualitySnapshot): Item | null {
  if (snapshot.hasSequenceGap !== true) return null;
  return { key: "gap", icon: "⌁", text: "Sequence gap in range", severity: "warn" };
}

export function DataQualityStrip(): ReactNode {
  const snapshot = useDataQuality();
  const items: Item[] = [silentItem(snapshot), dropsItem(snapshot), samplingItem(snapshot)];
  const skew = skewItem(snapshot);
  if (skew !== null) items.push(skew);
  const gap = gapItem(snapshot);
  if (gap !== null) items.push(gap);

  const degraded = items.some((i) => i.severity === "warn");

  return (
    <div className="dq-strip" data-degraded={degraded}>
      <div className="dq-strip__items" role="status" aria-label="Data quality">
        {items.map((item) => (
          <span key={item.key} className="dq-item" data-severity={item.severity}>
            <i className="dq-item__icon" aria-hidden="true">
              {item.icon}
            </i>
            <span>{item.text}</span>
          </span>
        ))}
      </div>
      <ServiceSwitcher />
    </div>
  );
}
